import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import CartDrawer from "./CartDrawer";

const Navbar: React.FC = () => {
    const { isAuthenticated, isAdmin, user, logout } = useAuth();
    const { items, setIsOpen: setCartOpen } = useCart();
    const navigate = useNavigate();
    const location = useLocation();
    const [isMenuOpen, setIsMenuOpen] = useState(false);

    const handleLogout = async () => {
        await logout();
        navigate("/");
    };

    const isActive = (path: string) => location.pathname === path;

    return (
        <>
        <nav className="fixed top-0 left-0 right-0 z-50 bg-black border-b border-white/10">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between h-16">
                    {/* Logo */}
                    <Link to="/" className="flex items-center">
                        <div className="relative">
                            <img
                                src="/logo.png"
                                alt="LYCA"
                                className="h-10"
                            />
                            <div className="absolute inset-0 bg-black/40 rounded"></div>
                        </div>
                    </Link>

                    {/* Desktop Navigation */}
                    <div className="hidden md:flex items-center space-x-8">
                        <Link
                            to="/"
                            className={`transition-colors duration-200 ${
                                isActive("/")
                                    ? "text-accent"
                                    : "text-gray-300 hover:text-white"
                            }`}
                        >
                            Accueil
                        </Link>
                        <Link
                            to="/contact"
                            className={`transition-colors duration-200 ${
                                isActive("/contact")
                                    ? "text-accent"
                                    : "text-gray-300 hover:text-white"
                            }`}
                        >
                            Contact
                        </Link>

                        {isAuthenticated && !isAdmin && (
                            <>
                                <Link
                                    to="/products"
                                    className={`transition-colors duration-200 ${
                                        isActive("/products")
                                            ? "text-accent"
                                            : "text-gray-300 hover:text-white"
                                    }`}
                                >
                                    Produits
                                </Link>
                                <Link
                                    to="/profile"
                                    className={`transition-colors duration-200 ${
                                        isActive("/profile")
                                            ? "text-accent"
                                            : "text-gray-300 hover:text-white"
                                    }`}
                                >
                                    Profil
                                </Link>
                            </>
                        )}

                        {isAdmin && (
                            <>
                                <Link
                                    to="/admin"
                                    className={`transition-colors duration-200 ${
                                        isActive("/admin")
                                            ? "text-accent"
                                            : "text-gray-300 hover:text-white"
                                    }`}
                                >
                                    Dashboard
                                </Link>
                                <Link
                                    to="/admin/categories"
                                    className={`transition-colors duration-200 ${
                                        isActive("/admin/categories")
                                            ? "text-accent"
                                            : "text-gray-300 hover:text-white"
                                    }`}
                                >
                                    Categories
                                </Link>
                                <Link
                                    to="/admin/products"
                                    className={`transition-colors duration-200 ${
                                        isActive("/admin/products")
                                            ? "text-accent"
                                            : "text-gray-300 hover:text-white"
                                    }`}
                                >
                                    Produits
                                </Link>
                                <Link
                                    to="/admin/clients"
                                    className={`transition-colors duration-200 ${
                                        isActive("/admin/clients")
                                            ? "text-accent"
                                            : "text-gray-300 hover:text-white"
                                    }`}
                                >
                                    Clients
                                </Link>
                                <Link
                                    to="/admin/telegram"
                                    className={`transition-colors duration-200 ${
                                        isActive("/admin/telegram")
                                            ? "text-accent"
                                            : "text-gray-300 hover:text-white"
                                    }`}
                                >
                                    Telegram
                                </Link>
                            </>
                        )}
                    </div>

                    {/* Auth Buttons */}
                    <div className="hidden md:flex items-center space-x-4">
                        {isAuthenticated ? (
                            <div className="flex items-center space-x-4">
                                <span className="text-gray-300 text-sm">
                                    {user?.username}
                                    {isAdmin && (
                                        <span className="ml-2 text-accent text-xs">
                                            (Admin)
                                        </span>
                                    )}
                                </span>
                                {!isAdmin && (
                                    <button
                                        onClick={() => setCartOpen(true)}
                                        className="relative text-gray-300 hover:text-accent transition-colors"
                                    >
                                        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                                d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                                        </svg>
                                        {items.length > 0 && (
                                            <span className="absolute -top-1.5 -right-1.5 bg-accent text-primary-dark text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center">
                                                {items.length}
                                            </span>
                                        )}
                                    </button>
                                )}
                                <button
                                    onClick={handleLogout}
                                    className="btn-secondary text-sm py-1.5 px-4"
                                >
                                    Deconnexion
                                </button>
                            </div>
                        ) : (
                            <>
                                <Link
                                    to="/login"
                                    className="btn-secondary text-sm py-1.5 px-4"
                                >
                                    Connexion
                                </Link>
                                <Link
                                    to="/register"
                                    className="btn-primary text-sm py-1.5 px-4"
                                >
                                    Inscription
                                </Link>
                            </>
                        )}
                    </div>

                    {/* Mobile: panier + burger */}
                    <div className="md:hidden flex items-center space-x-3">
                        {isAuthenticated && !isAdmin && (
                            <button
                                onClick={() => setCartOpen(true)}
                                className="relative text-gray-300 hover:text-accent transition-colors"
                            >
                                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                        d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                                </svg>
                                {items.length > 0 && (
                                    <span className="absolute -top-1.5 -right-1.5 bg-accent text-primary-dark text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center">
                                        {items.length}
                                    </span>
                                )}
                            </button>
                        )}
                        <button
                            onClick={() => setIsMenuOpen(!isMenuOpen)}
                            className="text-gray-300 hover:text-white"
                        >
                        <svg
                            className="h-6 w-6"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            {isMenuOpen ? (
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M6 18L18 6M6 6l12 12"
                                />
                            ) : (
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M4 6h16M4 12h16M4 18h16"
                                />
                            )}
                        </svg>
                    </button>
                    </div>
                </div>

                {/* Mobile menu */}
                {isMenuOpen && (
                    <div className="md:hidden py-4 border-t border-white/10">
                        <div className="flex flex-col space-y-4">
                            <Link
                                to="/"
                                className="text-gray-300 hover:text-white"
                                onClick={() => setIsMenuOpen(false)}
                            >
                                Accueil
                            </Link>
                            <Link
                                to="/contact"
                                className="text-gray-300 hover:text-white"
                                onClick={() => setIsMenuOpen(false)}
                            >
                                Contact
                            </Link>

                            {isAuthenticated && !isAdmin && (
                                <>
                                    <Link
                                        to="/products"
                                        className="text-gray-300 hover:text-white"
                                        onClick={() => setIsMenuOpen(false)}
                                    >
                                        Produits
                                    </Link>
                                    <Link
                                        to="/profile"
                                        className="text-gray-300 hover:text-white"
                                        onClick={() => setIsMenuOpen(false)}
                                    >
                                        Profil
                                    </Link>
                                </>
                            )}

                            {isAdmin && (
                                <>
                                    <Link
                                        to="/admin"
                                        className="text-gray-300 hover:text-white"
                                        onClick={() => setIsMenuOpen(false)}
                                    >
                                        Dashboard
                                    </Link>
                                    <Link
                                        to="/admin/categories"
                                        className="text-gray-300 hover:text-white"
                                        onClick={() => setIsMenuOpen(false)}
                                    >
                                        Categories
                                    </Link>
                                    <Link
                                        to="/admin/products"
                                        className="text-gray-300 hover:text-white"
                                        onClick={() => setIsMenuOpen(false)}
                                    >
                                        Produits
                                    </Link>
                                    <Link
                                        to="/admin/clients"
                                        className="text-gray-300 hover:text-white"
                                        onClick={() => setIsMenuOpen(false)}
                                    >
                                        Clients
                                    </Link>
                                    <Link
                                        to="/admin/telegram"
                                        className="text-gray-300 hover:text-white"
                                        onClick={() => setIsMenuOpen(false)}
                                    >
                                        Telegram
                                    </Link>
                                </>
                            )}

                            <div className="pt-4 border-t border-white/10">
                                {isAuthenticated ? (
                                    <button
                                        onClick={handleLogout}
                                        className="btn-secondary w-full"
                                    >
                                        Deconnexion
                                    </button>
                                ) : (
                                    <div className="flex flex-col space-y-2">
                                        <Link
                                            to="/login"
                                            className="btn-secondary text-center"
                                            onClick={() => setIsMenuOpen(false)}
                                        >
                                            Connexion
                                        </Link>
                                        <Link
                                            to="/register"
                                            className="btn-primary text-center"
                                            onClick={() => setIsMenuOpen(false)}
                                        >
                                            Inscription
                                        </Link>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </nav>
        <CartDrawer />
        </>
    );
};

export default Navbar;
